/**
 * <s-cod-checkout> — "Order Now - Cash on Delivery" action on the product page.
 * Adds the selected variant and quantity from the product form to the cart,
 * then sends the customer to checkout, where they choose Cash on Delivery.
 *
 * Commands (dispatched via commandfor/command on the descendant button):
 *   --checkout — add the product form's item to the cart, then go to checkout.
 *
 * Element dataset:
 *   data-form-id       — id of the product <form> to read the variant and quantity from.
 *   data-cart-add-url  — routes.cart_add_url (".js" is appended for the AJAX API).
 *   data-checkout-url  — storefront checkout path.
 * Child markup:
 *   [data-cod-error]   — hidden alert shown if the cart update fails.
 */
class SCodCheckout extends HTMLElement {
  #ac;
  #busy = false;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('command', (event) => {
      if (event.command !== '--checkout') return;
      this.#checkout(event.source);
    }, { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async #checkout(button) {
    if (this.#busy) return;
    const form = document.getElementById(this.dataset.formId);
    const error = this.querySelector('[data-cod-error]');
    if (!form) return;

    this.#busy = true;
    button?.setAttribute('aria-busy', 'true');
    if (error) error.hidden = true;

    try {
      const response = await fetch(`${this.dataset.cartAddUrl}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      if (!response.ok) throw new Error(`Cart add failed: ${response.status}`);
      window.location.assign(this.dataset.checkoutUrl);
    } catch (err) {
      console.error(err);
      if (error) error.hidden = false;
      this.#busy = false;
      button?.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('s-cod-checkout')) {
  customElements.define('s-cod-checkout', SCodCheckout);
}
